import React, { useCallback, useMemo } from "react";
import { IconButton, Icon } from "@alm/flow-ds/components";
import classnames from "classnames";
import FocusLock from "react-focus-lock";
import { Transition, animated } from "react-spring/renderprops";
import ClickOutside from "react-click-outside";
import { useTranslation } from "react-i18next";

interface Props {
	title: string;
	setIsTrayOpen: React.Dispatch<React.SetStateAction<boolean>>;
	children: React.ReactNode;
	align?: string;
	isTrayOpen: boolean;
	className?: string;
	closeOnClickOutside?: boolean;
}

function SideTray({
	isTrayOpen,
	title,
	setIsTrayOpen,
	children,
	align = "right",
	className,
	closeOnClickOutside = true,
}: Props) {
	const { t } = useTranslation();

	// Animate left/right depending on RTL position passed
	const animate =
		align === "right" ? "translateX(100%)" : "translateX(-100%)";

	/**
	 * Close tray when ESC
	 */
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Escape") {
				setIsTrayOpen(false);
			}
		},
		[setIsTrayOpen]
	);

	/**
	 * Close tray when click outside
	 */
	const clickOutsideProps = useMemo(
		() =>
			closeOnClickOutside
				? { onClickOutside: () => setIsTrayOpen(false) }
				: {},
		[closeOnClickOutside, setIsTrayOpen]
	);

	return (
		<Transition
			items={isTrayOpen}
			config={{
				tension: 500,
				friction: 50,
				duration: 150,
			}}
			from={{
				transform: animate,
			}}
			enter={{
				transform: "translateX(0)",
			}}
			leave={{
				transform: animate,
			}}>
			{isTrayOpen =>
				isTrayOpen &&
				(props => (
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					<ClickOutside<any> {...clickOutsideProps}>
						<animated.div
							style={props}
							role="dialog"
							aria-label={title}
							className={classnames(
								"sidetray-container",
								"sidetray-right",
								className
							)}
							onKeyDown={handleKeyDown}>
							<FocusLock returnFocus={true} noFocusGuards>
								<div className="sidetray__header">
									{/* <h4 className="sidetray__title" id={title}>
										{title}
									</h4> */}
									<IconButton
										label={t("main.icons.closeSideTray")}
										name={t("main.icons.closeSideTray")}
										className="btn-basic--dark"
										icon={
											<Icon
												type={"times " + align}
												weight="l"
											/>
										}
										onClick={() => {
											setIsTrayOpen(false);
										}}
									/>
								</div>
								{children}
							</FocusLock>
						</animated.div>
					</ClickOutside>
				))
			}
		</Transition>
	);
}

export default SideTray;
